import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { collection, doc, setDoc, updateDoc, serverTimestamp, onSnapshot, query, where, getDocs, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { handleAsyncError, logError } from '../utils/errorHandler';

const CourierContext = createContext();

export const useCourier = () => {
  const context = useContext(CourierContext);
  if (!context) {
    throw new Error('useCourier must be used within CourierProvider');
  }
  return context;
};

export const CourierProvider = ({ children }) => {
  const [courierUser, setCourierUser] = useState(null);
  const [courierLoading, setLoading] = useState(true);
  const [courierError, setError] = useState(null);
  const [assignedOrders, setAssignedOrders] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [claimedOrders, setClaimedOrders] = useState([]);
  const [courierLocation, setCourierLocation] = useState(null);
  const [locationWatcher, setLocationWatcher] = useState(null);

  // Set up real-time listener for auth state
  useEffect(() => {
    let unsubscribe = null;

    const setupAuthListener = async () => {
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        try {
          if (user) {
            // Check if user is a courier by looking in couriers collection
            const courierDocRef = doc(db, 'couriers', user.uid);
            const courierDocSnap = await getDoc(courierDocRef);

            let courierData = null;

            if (courierDocSnap.exists()) {
              courierData = courierDocSnap.data();
            } else {
              try {
                const couriersRef = collection(db, 'couriers');
                const q = query(couriersRef, where('userId', '==', user.uid));
                const snapshot = await getDocs(q);

                if (!snapshot.empty) {
                  courierData = snapshot.docs[0].data();
                }
              } catch (queryError) {
                logError(queryError, { context: 'CourierContext - Legacy courier query fallback' });
              }
            }

            if (courierData) {
              setCourierUser({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || 'Courier',
                ...courierData
              });

              // Set up real-time listener for assigned orders
              const ordersCollection = collection(db, 'orders');
              const assignedOrdersQuery = query(ordersCollection, where('assignedCourier', '==', user.uid));
              
              const unsubscribeAssignedOrders = onSnapshot(
                assignedOrdersQuery,
                (snapshot) => {
                  const orders = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                  }));
                  setAssignedOrders(orders);
                },
                (ordersError) => {
                  logError(ordersError, { context: 'CourierContext - Assigned Orders listener' });
                  setAssignedOrders([]);
                }
              );

              // Set up real-time listener for available orders (shipped status, not yet claimed)
              // Note: Filters for orders where claimedBy is null OR doesn't exist (for orders created before this feature)
              const availableOrdersQuery = query(
                ordersCollection,
                where('status', '==', 'shipped')
              );
              
              const unsubscribeAvailableOrders = onSnapshot(
                availableOrdersQuery,
                (snapshot) => {
                  const orders = snapshot.docs
                    .map(doc => ({
                      id: doc.id,
                      ...doc.data()
                    }))
                    .filter(order => !order.claimedBy); // Filter out orders that have been claimed
                  setAvailableOrders(orders);
                },
                (ordersError) => {
                  logError(ordersError, { context: 'CourierContext - Available Orders listener' });
                  setAvailableOrders([]);
                }
              );

              // Set up real-time listener for claimed orders (claimed by this courier)
              const claimedOrdersQuery = query(
                ordersCollection,
                where('claimedBy', '==', user.uid)
              );
              
              const unsubscribeClaimedOrders = onSnapshot(
                claimedOrdersQuery,
                (snapshot) => {
                  const orders = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                  }));
                  setClaimedOrders(orders);
                },
                (ordersError) => {
                  logError(ordersError, { context: 'CourierContext - Claimed Orders listener' });
                  setClaimedOrders([]);
                }
              );

              return () => {
                unsubscribeAssignedOrders();
                unsubscribeAvailableOrders();
                unsubscribeClaimedOrders();
              };
            } else {
              setCourierUser({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || 'Courier',
                status: 'offline',
                profileIncomplete: true
              });
              setError('Courier profile is incomplete. Contact administrator to complete setup.');
            }
          } else {
            setCourierUser(null);
            setAssignedOrders([]);
            setAvailableOrders([]);
            setClaimedOrders([]);
            setLocationWatcher((previousWatcher) => {
              if (previousWatcher !== null) {
                navigator.geolocation.clearWatch(previousWatcher);
              }
              return null;
            });
          }
        } catch (error) {
          logError(error, { context: 'CourierContext - Auth Setup' });
          setError('Failed to load courier information');
        } finally {
          setLoading(false);
        }
      });
    };

    setupAuthListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Courier Login
  const courierLogin = async (email, password) => {
    const result = await handleAsyncError(async () => {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    }, { action: 'courier_login', email });

    return result;
  };

  // Courier Signup (admin only)
  const courierSignup = async (email, password, displayName, phone, vehicleNumber) => {
    const result = await handleAsyncError(async () => {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update profile
      await updateProfile(user, { displayName });

      // Create courier document
      await setDoc(doc(db, 'couriers', user.uid), {
        userId: user.uid,
        email,
        displayName,
        phone,
        vehicleNumber,
        status: 'available', // available, on-delivery, offline
        currentLocation: null,
        totalDeliveries: 0,
        rating: 5,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return user;
    }, { action: 'courier_signup', email });

    return result;
  };

  // Courier Logout
  const courierLogout = async () => {
    const result = await handleAsyncError(async () => {
      stopLocationTracking();
      await signOut(auth);
      setCourierUser(null);
      setAssignedOrders([]);
      setAvailableOrders([]);
      setClaimedOrders([]);
    }, { action: 'courier_logout' });

    return result;
  };

  // Update courier location
  const updateLocation = async (latitude, longitude) => {
    if (!courierUser) return;

    const result = await handleAsyncError(async () => {
      const courierDoc = doc(db, 'couriers', courierUser.uid);
      await setDoc(courierDoc, {
        currentLocation: {
          latitude,
          longitude,
          timestamp: serverTimestamp(),
          accuracy: null
        },
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Also update the orders that are currently out for delivery with courier location
      if (claimedOrders && claimedOrders.length > 0) {
        const outForDeliveryOrders = claimedOrders.filter(o => o.status === 'out_for_delivery');
        console.log(`🚗 Updating locations for ${outForDeliveryOrders.length} out-for-delivery orders`);
        
        for (const order of outForDeliveryOrders) {
          try {
            // Fetch the full order document to ensure we have userId
            const fullOrderDoc = await getDoc(doc(db, 'orders', order.id));
            const fullOrderData = fullOrderDoc.data();
            const userId = order.userId || fullOrderData?.userId || fullOrderData?.customerId;

            if (!userId) {
              console.warn(`⚠️ Order ${order.id} has no userId, cannot update customer's order`);
              continue;
            }

            const locationData = {
              courierLocation: {
                latitude,
                longitude,
                timestamp: serverTimestamp()
              },
              lastUpdated: serverTimestamp()
            };

            // Update main orders collection
            await updateDoc(doc(db, 'orders', order.id), locationData);
            console.log(`📍 Updated location in main /orders/${order.id}`);

            // Also update in customer's orders subcollection
            await updateDoc(doc(db, 'users', userId, 'orders', order.id), locationData);
            console.log(`📍 Updated location in /users/${userId}/orders/${order.id}`);
          } catch (error) {
            console.error(`❌ Error updating location for order ${order.id}:`, error.message);
            logError(error, { context: 'CourierContext - Update order location', orderId: order.id });
          }
        }
      }

      setCourierLocation({ latitude, longitude, timestamp: new Date() });
    }, { action: 'update_courier_location' });

    return result;
  };

  // Start tracking courier location
  const startLocationTracking = (useHighAccuracy = false) => {
    if (!('geolocation' in navigator)) {
      setError('Geolocation is not supported by this browser');
      return;
    }

    const options = {
      enableHighAccuracy: useHighAccuracy,
      maximumAge: 5000, // Cache location for 5 seconds
      timeout: 10000 // 10 second timeout
    };

    const watcher = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        updateLocation(latitude, longitude);
        setError(null);
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';
        if (error.code === 1) {
          errorMessage = 'Permission denied. Please enable location services.';
        } else if (error.code === 2) {
          errorMessage = 'Position unavailable';
        } else if (error.code === 3) {
          errorMessage = 'Request timeout';
        }
        setError(errorMessage);
        logError(error, { context: 'Geolocation - Location Tracking' });
      },
      options
    );

    setLocationWatcher(watcher);
  };

  // Stop tracking courier location
  const stopLocationTracking = () => {
    if (locationWatcher !== null) {
      navigator.geolocation.clearWatch(locationWatcher);
      setLocationWatcher(null);
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, status, notes = '', deliveryPhoto = null) => {
    if (!courierUser) return;

    const result = await handleAsyncError(async () => {
      const orderDoc = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderDoc);
      
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }

      const orderData = orderSnap.data();
      const userId = orderData.userId || orderData.customerId;

      if (!userId) {
        console.warn(`⚠️ Order ${orderId} has no userId field`, orderData);
        throw new Error('Order has no associated user');
      }

      const updates = {
        status,
        lastUpdated: serverTimestamp(),
      };

      if (notes) {
        updates.courierNotes = notes;
      }

      // Handle delivery photo conversion to base64
      if (deliveryPhoto && status === 'delivered') {
        try {
          console.log(`📸 Converting delivery photo for order ${orderId}...`);
          const reader = new FileReader();
          
          await new Promise((resolve, reject) => {
            reader.onload = () => {
              const base64String = reader.result;
              updates.deliveryPhotoBase64 = base64String;
              updates.deliveryPhotoUploadedAt = serverTimestamp();
              console.log(`✅ Delivery photo converted to base64`);
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(deliveryPhoto);
          });
        } catch (photoError) {
          console.warn(`⚠️ Failed to convert delivery photo:`, photoError.message);
          // Continue with status update even if photo conversion fails
        }
      }

      if (status === 'delivered') {
        updates.deliveredAt = serverTimestamp();
      } else if (status === 'out_for_delivery') {
        updates.outForDeliveryAt = serverTimestamp();
      }

      // Update in main orders collection
      console.log(`📝 Updating order ${orderId} status to ${status} in main collection`);
      await updateDoc(orderDoc, updates);

      // Also update in user's orders subcollection
      console.log(`📝 Updating order ${orderId} status to ${status} in user ${userId} collection`);
      const userOrderDoc = doc(db, 'users', userId, 'orders', orderId);
      await updateDoc(userOrderDoc, updates);
      
      console.log(`✅ Successfully updated order ${orderId} in both collections`);
    }, { action: 'update_order_status', orderId, status });

    return result;
  };

  // Update courier status (available, on-delivery, offline)
  const updateCourierStatus = async (status) => {
    if (!courierUser) return;

    const result = await handleAsyncError(async () => {
      const courierDoc = doc(db, 'couriers', courierUser.uid);
      await setDoc(courierDoc, {
        status,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setCourierUser(prev => ({ ...prev, status }));
    }, { action: 'update_courier_status', status });

    return result;
  };

  // Claim an order for delivery
  const claimOrder = async (orderId) => {
    if (!courierUser) return;

    const result = await handleAsyncError(async () => {
      const orderDoc = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderDoc);
      
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }

      const orderData = orderSnap.data();
      const userId = orderData.userId || orderData.customerId;

      const updateData = {
        claimedBy: courierUser.uid,
        claimedByName: courierUser.displayName,
        courierPhone: courierUser.phone,
        courierName: courierUser.displayName,
        claimedAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      // Update main orders collection
      await updateDoc(orderDoc, updateData);

      // Also update in user's orders collection
      if (userId) {
        const userOrderDoc = doc(db, 'users', userId, 'orders', orderId);
        await updateDoc(userOrderDoc, updateData);
        console.log(`✅ Order ${orderId} claimed by ${courierUser.displayName} (${courierUser.phone})`);
      }
    }, { action: 'claim_order', orderId });

    return result;
  };

  // Unclaim an order (courier cancels claim)
  const unclaimOrder = async (orderId) => {
    if (!courierUser) return;

    const result = await handleAsyncError(async () => {
      const orderDoc = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderDoc);
      
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }

      const orderData = orderSnap.data();
      const userId = orderData.userId || orderData.customerId;

      const updateData = {
        claimedBy: null,
        claimedByName: null,
        courierPhone: null,
        courierName: null,
        claimedAt: null,
        lastUpdated: serverTimestamp()
      };

      // Update main orders collection
      await updateDoc(orderDoc, updateData);

      // Also update in user's orders collection
      if (userId) {
        const userOrderDoc = doc(db, 'users', userId, 'orders', orderId);
        await updateDoc(userOrderDoc, updateData);
        console.log(`✅ Order ${orderId} unclaimed`);
      }
    }, { action: 'unclaim_order', orderId });

    return result;
  };

  const value = {
    courierUser,
    courierLoading,
    courierError,
    assignedOrders,
    availableOrders,
    claimedOrders,
    courierLocation,
    courierLogin,
    courierSignup,
    courierLogout,
    updateLocation,
    startLocationTracking,
    stopLocationTracking,
    updateOrderStatus,
    updateCourierStatus,
    claimOrder,
    unclaimOrder
  };

  return (
    <CourierContext.Provider value={value}>
      {children}
    </CourierContext.Provider>
  );
};
